import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { renderProfileParts } from "./parts";

/**
 * Trading-card template — Pokémon/Magic-style framed portrait.
 *
 *   ┌───── ◊ USERNAME ◊ ─────┐
 *   │                        │
 *   │      [big avatar]      │  ← "art" panel
 *   │                        │
 *   ├────── attacks ─────────┤
 *   │ 🔗 socials / links     │
 *   │ 🎵 audio               │
 *   ├─ stats ────────────────┤
 *   │ views · location · clk │
 *   └────────────────────────┘
 *
 * Holographic shimmer via a conic-gradient overlay that shifts with
 * tilt. Thick decorative border. Intended to feel like the user is a
 * collectible card on display.
 */
export function renderTradingCard(
  config: ProfileConfig,
  options: { gateOpened: Promise<void> } = { gateOpened: Promise.resolve() },
): HTMLElement {
  const p = renderProfileParts(config, options);
  p.card.classList.add("card--trading");

  const holo = el("div", { class: "trading__holo", "aria-hidden": "true" });

  const titleBar = el("div", { class: "trading__title" }, [
    el("span", { class: "trading__title-gem" }, ["◊"]),
    el("span", { class: "card__name-row trading__name" }, [p.username, p.badges]),
    el("span", { class: "trading__title-gem" }, ["◊"]),
  ]);

  const art = el("div", { class: "trading__art" }, [p.avatar]);

  const flavor = el("div", { class: "trading__flavor" }, [p.description]);

  const actions = el("div", { class: "trading__actions" });
  if (p.socials) actions.append(p.socials);
  if (p.links) actions.append(p.links);
  if (p.widgets) actions.append(p.widgets);
  if (p.audio) actions.append(p.audio);

  const stats = el("div", { class: "trading__stats" });
  const vc = p.meta.getInline("views");    if (vc) stats.append(vc);
  const loc = p.meta.getInline("location"); if (loc) stats.append(loc);
  const clk = p.meta.getInline("clock");    if (clk) stats.append(clk);
  const ct = p.meta.getInline("contact");
  if (ct && p.contact) {
    const corner = el("div", { class: "card__corner card__corner--top-right" }, [ct]);
    p.card.append(corner);
  }

  p.card.append(holo, titleBar, art, flavor, actions);
  if (stats.childElementCount > 0) p.card.append(stats);
  p.meta.applyCornerZones();
  return p.card;
}
